import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import WelcomeStep from './steps/welcome-step';
import SubscriptionStep from './steps/subscription-step';
import CompanyStep from './steps/company-step';

interface OnboardingModalProps {
  isOpen: boolean;
  onComplete: () => void;
  hasActiveSubscription?: boolean;
}

const OnboardingModal: React.FC<OnboardingModalProps> = observer(({ isOpen, onComplete, hasActiveSubscription = false }) => {
  const [currentStep, setCurrentStep] = useState(hasActiveSubscription ? 3 : 1);

  useEffect(() => {
    // Si l'utilisateur a déjà un abonnement, commencer à l'étape 3 (création entreprise)
    if (isOpen && hasActiveSubscription) {
      setCurrentStep(3);
    } else if (isOpen) {
      setCurrentStep(1);
    }
  }, [isOpen, hasActiveSubscription]);

  if (!isOpen) return null;

  const handleNextStep = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePreviousStep = () => {
    // Si l'utilisateur a déjà un abonnement, ne pas revenir en arrière depuis l'étape 3
    if (hasActiveSubscription && currentStep === 3) {
      return;
    }
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handlePlanSelected = (planId: string) => {
    localStorage.setItem('onboarding_selected_plan', planId);
    handleNextStep();
  };

  const handleCompanyCreated = () => {
    onComplete();
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <WelcomeStep onNext={handleNextStep} />;
      case 2:
        return (
          <SubscriptionStep
            onPlanSelected={handlePlanSelected}
            onBack={handlePreviousStep}
          />
        );
      case 3:
        return (
          <CompanyStep
            onComplete={handleCompanyCreated}
            onBack={handlePreviousStep}
            hideBackButton={hasActiveSubscription}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-white dark:bg-gray-800 rounded-2xl shadow-2xl">
        {/* Progress bar */}
        <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-center p-6">
            {[1, 2, 3].map((step) => (
              <React.Fragment key={step}>
                <div className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                      step === currentStep
                        ? 'bg-primary text-white scale-110'
                        : step < currentStep || (hasActiveSubscription && step < 3)
                        ? 'bg-primary-light text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-500'
                    }`}
                  >
                    {(step < currentStep || (hasActiveSubscription && step < 3)) ? (
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    ) : (
                      step
                    )}
                  </div>
                  <span className="mt-2 text-xs font-medium text-gray-600 dark:text-gray-400">
                    {step === 1 && 'Bienvenue'}
                    {step === 2 && 'Abonnement'}
                    {step === 3 && 'Entreprise'}
                  </span>
                </div>
                {step < 3 && (
                  <div
                    className={`w-24 h-1 mx-4 transition-all ${
                      step < currentStep || (hasActiveSubscription && step < 3)
                        ? 'bg-primary-light'
                        : 'bg-gray-200 dark:bg-gray-700'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Step content */}
        <div className="p-8">{renderStep()}</div>
      </div>
    </div>
  );
});

export default OnboardingModal;
